import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Api from '../utils/Api';
import LessonOptions from '../utils/LessonOptions';
// v0.10.13 — 背景图 URL 转义统一入口 (见 CardBackground.escapeCssUrl 顶部注释)
// v0.17.4 — 另引入 builtinBgStoreValue: 内置图存库要剥掉 ?v=2 版本戳 (见下方 isSel/pickBuiltinBg 注释)
import { escapeCssUrl, builtinBgStoreValue } from '../utils/CardBackground';
// Phase D2 (v0.18.16): 背景图上传改走 ziven-core 共享 uploadFile(), 随 storage_provider
// 自动切 COS / 七牛 / 本地。本调用点只要 URL (存的是完整 URL, 不入库 key), 用最薄的那个。
import { uploadFile } from 'ext:ziiven/ziven-core/common/helpers/uploadFile';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/**
 * CardTemplateEditorModal — 新建 / 编辑卡种
 *
 * v0.8.0 改造 (辉哥 10/6 拍板, 卡片整卡背景改造):
 *   - 颜色选择器 → 背景图选择器 (10 张内置 + 用户自定义 + COS 上传)
 *   - 描述默认值去掉 (desc: '1对1' → ''), 只保留 placeholder
 *   - 拖拽手柄 / 字段顺序 / 校验跟 v0.6.5a 一致
 *
 * v0.6.5a 改造 (辉哥 9/26 12:30 拍板):
 *   - 课程分类 改 checkbox 多选 (data 字段 lesson_category_ids: string[])
 *   - 课程类型 dropdown 从分类行移到 时长行 (grid-2col 第二列), 仍是单选
 *   - 状态 改 Switch (Flarum 原生 Switch 组件, boolean state)
 *   - 字段顺序 (v6 final): 名称 / 课程分类 / 描述 / 颜色 / 价格·课时 / 时长·类型 / 状态
 *
 * attrs:
 *   - card: null=新建, 模板对象=编辑 (后端 List 序列化 lesson_category_ids 是数组)
 *   - onClose: 关闭后回调 (父 tab 刷新数据用)
 *
 * SOP 97: <input> 禁止裸 bidi 属性 (Modal 渲染管线 bug)
 * SOP 98: app.translator.trans(key, params) 默认返数组, confirm/alert 必须传 extract=true
 */
export default class CardTemplateEditorModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.card = vnode.attrs.card;
    this.onCloseCb = vnode.attrs.onClose;
    this.saving = false;
    this.error = '';

    // 初始化表单 — 新建时空模板 (默认值), 编辑时拷贝
    //   v0.6.5a: lesson_category_ids 替代 lesson_category_id (数组 vs 字符串)
    //   v0.6.5d: price/lessons 默认值 (辉哥 14:11 反馈 bjxy 端默认值丢失)
    //   v0.8.0: desc 默认值 '1对1' 移除 (辉哥 10/6 拍板, 只留 placeholder)
    //           color 字段保留不删 (兼容 + 兜底), 但前端不再渲染, 主视觉用 bg_image
    //           bg_image 默认 '' (空, 后端兜底 background-color #1b1f26)
    this.form = this.card ? {
      ...this.card
    } : {
      name: '',
      desc: '',
      color: 'var(--zs-brand-2d7be5, #2D7BE5)',
      bg_image: '',
      price: 3000,
      lessons: 30,
      // v0.6.5 — 时长默认 60 → 45 (辉哥 9/26 SOP)
      minutes: 45,
      status: 'enabled',
      // v0.6.5a — 课程分类改成 checkbox 多选 (数组), 后端 casts = array
      //            新建默认 [], 用户进 modal 后勾选, validate 要求 >=1
      lesson_category_ids: [],
      // v0.6.5 — 课程类型仍是单选 (string id), 默认空让用户 dropdown 选
      lesson_type_id: ''
    };
    // v0.8.0 — 编辑老卡种: 若老数据没 bg_image 字段, 兜底空串
    if (typeof this.form.bg_image === 'undefined') this.form.bg_image = '';

    // v0.6.5 — fetch LessonOptions (分类/类型), 启动期 1 次, 卡渲染期间不阻塞
    LessonOptions.load();

    // v0.8.0 / v0.17.4 — 后台拉 10 张内置背景图 URL 列表 (card-bg/list 端点, 已登录可读)
    // 加载失败也不阻塞 — 提示用户但仍可手动输入 bg_image
    this.bgImages = []; // v0.17.4: [{key, filename, url(带?v=2), path(规范值)}, ...] 10 项
    this.bgImagesLoaded = false;
    this.customImages = []; // 用户本会话上传过的 [{url, name, isCustom:true}, ...]
    this.bgImageErr = ''; // 上传/校验错误提示
    this.bgImageBusy = false; // 上传中, 防止双击
    this.fetchBuiltinBgImages();
  }
  className() {
    // v0.6.5b — 加 .ZS-CardTemplateModal scope 让 less 只覆盖本 modal 样式,
    //            不影响其他 modal (BindCardModal / RoomEditorModal / ...)
    // v0.7.9d: 移动端全屏 sheet 对齐 6 个 modal
    return 'Modal--medium Modal--fullscreen-padded ZS-CardTemplateModal';
  }
  title() {
    return this.card ? '编辑卡种' : '新建卡种';
  }
  validate() {
    if (!this.form.name || !String(this.form.name).trim()) return '请填写名称';
    // v0.6.5a — 课程分类 checkbox 多选, 至少 1 个
    if (!Array.isArray(this.form.lesson_category_ids) || this.form.lesson_category_ids.length < 1) {
      return '请至少选择 1 个课程分类';
    }
    // v0.6.5a — 课程类型 仍是单选, 必填
    if (!this.form.lesson_type_id) return '请选择课程类型';
    const price = +this.form.price;
    if (!Number.isFinite(price) || price < 0) return '价格必须 ≥ 0';
    const lessons = +this.form.lessons;
    if (!Number.isInteger(lessons) || lessons < 0) return '课时数必须 ≥ 0 整数';
    const minutes = +this.form.minutes;
    if (!Number.isInteger(minutes) || minutes < 1) return '时长必须 ≥ 1 整数分钟';
    return null;
  }
  async submit() {
    const err = this.validate();
    if (err) {
      this.error = err;
      m.redraw();
      return;
    }
    this.error = '';
    this.saving = true;
    m.redraw();
    try {
      const payload = {
        name: String(this.form.name).trim(),
        desc: this.form.desc || '',
        color: this.form.color,
        // v0.8.0 — bg_image 字段传给后端, 空串 → 后端规范化成 null
        bg_image: this.form.bg_image || '',
        price: +this.form.price,
        lessons: +this.form.lessons,
        minutes: +this.form.minutes,
        status: this.form.status,
        // v0.6.5a — payload 改 lesson_category_ids (数组)
        lesson_category_ids: this.form.lesson_category_ids,
        // v0.6.5 — 课程类型仍是单选 id
        lesson_type_id: this.form.lesson_type_id
      };
      if (this.card && this.card.id) {
        await Api.update('card-templates', this.card.id, payload);
      } else {
        await Api.create('card-templates', payload);
      }
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      this.saving = false;
      // v0.17.65 — 为什么不能只看 `e.message` (改前这里只有 `|| '保存失败'`)
      //   后端抛 ValidationException 时 HTTP 是 422, **真实原因在 `e.response.errors[0].detail`**;
      //   `e.message` 只是请求层拼的 "Response code 422 (Unprocessable Entity)", 跟后端那句提示无关。
      //   后果: 卡种名重复 / 课次数非法这类**可操作**的原因被吞成「保存失败」, 用户无从改哪个字段。
      //   形状照抄同仓既有正确写法 (CalendarTab.js:2770 / modal/FixedLessonModal.js:593);
      //   兜底文案「保存失败」保留不变, 只是降为最后一档 (网络层异常 / 非 422 时仍命中)。
      this.error = errReason(e, '保存失败');
      console.error('[card-template-editor] save failed', e);
      m.redraw();
    }
  }

  // v0.6.5a — 课程分类 checkbox 列表
  // v0.6.5b — 显示全部 (含 disabled), 跟 v6 设计图一致
  lessonCategoriesCheckboxList() {
    const cats = LessonOptions.categories();
    const selected = this.form.lesson_category_ids || [];
    if (!cats || cats.length === 0) {
      return m("div", {
        className: "ZS-form-input",
        style: "background:var(--zs-surface-3-f5f5f5, #f5f5f5);color:var(--zs-text-2-666, #666);display:flex;align-items:center;gap:8px;"
      }, m("i", {
        className: "fas fa-tags"
      }), m("span", null, "\u6682\u65E0\u8BFE\u7A0B\u5206\u7C7B, \u8BF7\u5148\u5728\u540E\u53F0\u914D\u7F6E"));
    }
    return m("div", {
      className: "ZS-option-list"
    }, cats.map(c => {
      const checked = selected.indexOf(c.id) !== -1;
      const isDisabled = c.enabled === false;
      let badge;
      if (isDisabled) {
        badge = m("span", {
          className: "Badge--disabled"
        }, "\u5DF2\u7981\u7528");
      } else if (c.is_builtin) {
        badge = m("span", {
          className: "Badge--builtin"
        }, "\u5185\u7F6E");
      } else {
        badge = m("span", {
          className: "Badge--custom"
        }, "\u81EA\u5B9A\u4E49");
      }
      return m("label", {
        key: c.id,
        className: 'ZS-OptionCheckbox' + (checked ? ' checked' : '') + (isDisabled ? ' disabled' : '')
      }, m("input", {
        type: "checkbox",
        checked: checked,
        disabled: isDisabled,
        onchange: e => {
          e.stopPropagation();
          if (isDisabled) return;
          this.toggleCategory(c.id);
        }
      }), m("span", {
        className: "ZS-OptionCheckbox-name"
      }, c.name), badge);
    }));
  }
  toggleCategory(id) {
    const cat = LessonOptions.findCategory(id);
    if (cat && cat.enabled === false) return;
    const arr = Array.isArray(this.form.lesson_category_ids) ? this.form.lesson_category_ids : [];
    const idx = arr.indexOf(id);
    if (idx === -1) {
      arr.push(id);
    } else {
      arr.splice(idx, 1);
    }
    this.form.lesson_category_ids = arr;
    m.redraw();
  }
  lessonTypeDropdown() {
    const types = LessonOptions.typesEnabled();
    const current = LessonOptions.findType(this.form.lesson_type_id);
    const label = current ? current.name : this.form.lesson_type_id || '请选择课程类型';
    const builtinLabel = current && current.is_builtin ? '内置' : '';
    if (!types || types.length === 0) {
      return m("div", {
        className: "ZS-form-input"
      }, label);
    }
    return m(Dropdown, {
      buttonClassName: "ZS-form-input ZS-lesson-type-dropdown",
      icon: "fas fa-th-large",
      label: [label, builtinLabel && m("span", {
        className: "Badge--builtin"
      }, builtinLabel)]
    }, types.map(t => m(Button, {
      key: t.id,
      active: t.id === this.form.lesson_type_id,
      onclick: () => {
        this.form.lesson_type_id = t.id;
        m.redraw();
      }
    }, m("i", {
      className: "fas fa-th-large",
      style: "color:var(--zs-brand-2d7be5, #2d7be5);margin-right:6px;"
    }), t.name, t.is_builtin && m("span", {
      className: "Badge--builtin"
    }, "\u5185\u7F6E"))));
  }

  // ============================================================
  // v0.8.0 — 背景图选择器 (辉哥 10/6 拍板, 替代原 .ZS-color-row/.ZS-color-cell)
  // ============================================================

  /** 拉 10 张内置图 URL 列表 (后端 /api/ziiven/scheduler/card-bg/list) */
  async fetchBuiltinBgImages() {
    try {
      const r = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/card-bg/list'
      });
      if (r && r.success && Array.isArray(r.data && r.data.images)) {
        this.bgImages = r.data.images;
        this.bgImagesLoaded = true;
        // 编辑老卡种: 若 bg_image 是内置图 URL, 把对应 cell 标 selected (默认走 form.bg_image 即可)
        m.redraw();
      }
    } catch (e) {
      console.error('[card-template-editor] bg-image list failed', e);
      this.bgImageErr = '内置背景图列表加载失败, 请稍后重试';
      m.redraw();
    }
  }

  /**
   * 选择一个内置图
   *
   * v0.17.4: 存的是 **path 字段 (不带 ?v=2)**, 不是 url。
   *   card-bg/list 返的 url 带版本戳是为了缩略图破 7 天缓存; 但库里必须存规范值,
   *   因为 BgImageValidator 分支②要求前缀之后的后缀整段匹配 ^[A-Za-z0-9_-]{1,64}$,
   *   `n1?v=2` 会被后端 422 拒掉。
   *   渲染端由 CardBackground.cardBackgroundStyle() 统一补戳, 不依赖库里有没有戳。
   */
  pickBuiltinBg(img) {
    this.form.bg_image = img && img.path || img && img.url || '';
    m.redraw();
  }

  /** 删除一个自定义图引用 (本会话数组); 若删除的是当前选中, 重置为一个安全的默认内置图 */
  removeCustomBg(idx) {
    const removedUrl = this.customImages[idx] && this.customImages[idx].url;
    this.customImages.splice(idx, 1);
    if (removedUrl && this.form.bg_image === removedUrl) {
      // 兜底: 重置为第一个内置图; 如果内置图还没加载, 重置为空串
      // v0.17.4 — 用 path (规范值) 而不是 url (?v=2), 否则存库会被 BgImageValidator 422
      const fallback = this.bgImages && this.bgImages.length > 0 ? builtinBgStoreValue(this.bgImages[0].url) : '';
      this.form.bg_image = fallback;
    }
    m.redraw();
  }

  /** 点上传框 → 选文件 */
  triggerUpload() {
    if (this.bgImageBusy) return;
    if (!this.bgImageFileInput) {
      // 创建一个 lazy input 挂到 DOM 上, 不挂到 ZS-CardTemplateModal 容器, 避免 modal 卸载时被清
      const el = document.createElement('input');
      el.type = 'file';
      el.accept = 'image/jpeg,image/png,image/webp';
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.addEventListener('change', ev => {
        this.onFilePicked(ev);
      });
      this.bgImageFileInput = el;
    }
    this.bgImageFileInput.click();
  }

  /** 选完文件: 校验 + 上传 COS + 插到数组头 + scrollTop 归零 + 选中 */
  async onFilePicked(ev) {
    const file = ev.target.files && ev.target.files[0];
    // 清 value, 允许同一文件再次选
    ev.target.value = '';
    if (!file) return;

    // 1. 大小限制: ≤ 2MB
    if (file.size > 2 * 1024 * 1024) {
      this.bgImageErr = '图片超过 2MB 限制 (' + Math.round(file.size / 1024) + ' KB)';
      m.redraw();
      return;
    }
    // 2. 类型限制: jpg/png/webp (浏览器给的 type 可能空, 也按扩展名兜底)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp'];
    if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
      this.bgImageErr = '仅支持 jpg / png / webp 格式 (当前: ' + (file.type || ext || '未知') + ')';
      m.redraw();
      return;
    }
    this.bgImageErr = '';
    this.bgImageBusy = true;
    m.redraw();
    try {
      // 3. Phase D2 (v0.18.16): 原来的「拿 scheduler COS 签名 → 拼 FormData → XHR 直传」
      //    已删除, 换成 ziven-core 的 uploadFile()。三档(COS/七牛/本地)统一返回绝对 URL。
      //    ⚠️ 前面的 jpg/png/webp 白名单**保留**: 本地档的后端扩展名白名单虽然也含这三个,
      //    但前端先拦一道能给用户「仅支持 jpg/png/webp」这种带当前格式的精确提示。
      if (typeof uploadFile !== 'function') {
        throw new Error('ziven-core 上传组件未就绪, 请刷新页面后重试');
      }
      const uploadedUrl = await uploadFile(file, {
        prefix: 'scheduler_card_bg'
      });

      // 4. 插数组头 + 滚动归零 + 选中
      this.customImages.unshift({
        url: uploadedUrl,
        name: file.name,
        isCustom: true
      });
      this.form.bg_image = uploadedUrl;
      // 滚动区 scrollTop=0 (下次 onmount 才渲染, 这里走 redraw 后再 querySelector)
      m.redraw();
      setTimeout(() => {
        const sc = document.querySelector('.ZS-CardTemplateModal .ZS-bgimage-scroll');
        if (sc) sc.scrollTop = 0;
      }, 0);
    } catch (e) {
      console.error('[card-template-editor] 上传失败', e);
      this.bgImageErr = '上传失败: ' + (e.message || e);
      m.redraw();
    } finally {
      this.bgImageBusy = false;
      m.redraw();
    }
  }

  /** 渲染背景图选择器 JSX */
  bgImagePicker() {
    const builtins = this.bgImages || [];
    const customs = this.customImages || [];
    const selected = this.form.bg_image || '';
    const builtinsCount = builtins.length;
    const customsCount = customs.length;
    return m("div", null, m("div", {
      className: "ZS-bgimage-label"
    }, "\u5361\u7247\u80CC\u666F"), m("div", {
      className: "ZS-bgimage-hint"
    }, "\u9009\u4E00\u5F20\u4F1A\u5458\u5361 / \u94F6\u884C\u5361\u8D28\u611F\u7684\u5E95\u56FE, \u80CC\u666F\u4F1A\u94FA\u6EE1\u6574\u5F20\u5361\u7247\u3002\u4E5F\u53EF\u4EE5\u4E0A\u4F20\u81EA\u5DF1\u7684\u56FE\u7247\u3002"), m("div", {
      className: "ZS-bgimage-scroll"
    }, m("div", {
      className: "ZS-bgimage-grid"
    }, customs.map((img, idx) => {
      const isSel = selected === img.url;
      return m("div", {
        key: 'custom-' + idx,
        className: 'ZS-bgimage-cell custom' + (isSel ? ' selected' : '')
        // v0.10.13 — 转义统一走共享 escapeCssUrl (先 '\' 后 '"' + 剥控制字符)
        ,
        style: 'background-image: url("' + escapeCssUrl(img.url) + '");',
        onclick: () => {
          this.form.bg_image = img.url;
          m.redraw();
        }
      }, isSel && m("span", {
        className: "ZS-bgimage-ck",
        title: "\u5DF2\u9009\u4E2D"
      }, m("i", {
        className: "fas fa-check"
      })), m("span", {
        className: "ZS-bgimage-del",
        title: "\u5220\u9664",
        onclick: e => {
          e.stopPropagation();
          this.removeCustomBg(idx);
        }
      }, "\xD7"));
    }), builtins.map(img => {
      // v0.17.4 — 选中判定按「规范值」比。
      //   DB 存量是不带戳的 /api/.../card-bg/n1, list 接口返的 url 带 ?v=2,
      //   直接字符串比永远 false ⇒ 已选中的内置图打不出勾。
      const isSel = builtinBgStoreValue(selected) === builtinBgStoreValue(img.url);
      return m("div", {
        key: img.key,
        className: 'ZS-bgimage-cell builtin' + (isSel ? ' selected' : '')
        // v0.10.13 — 转义统一走共享 escapeCssUrl (先 '\' 后 '"' + 剥控制字符)
        // v0.17.4 — 缩略图用带 ?v=2 的 url, 换图后选择器缩略图不会卡在 7 天缓存里
        ,
        style: 'background-image: url("' + escapeCssUrl(img.url) + '");',
        onclick: () => {
          this.pickBuiltinBg(img);
        }
      }, m("span", {
        className: "ZS-bgimage-tag-builtin"
      }, "\u5185\u7F6E"), isSel && m("span", {
        className: "ZS-bgimage-ck",
        title: "\u5DF2\u9009\u4E2D"
      }, m("i", {
        className: "fas fa-check"
      })));
    }))), m("div", {
      className: 'ZS-bgimage-upload' + (this.bgImageBusy ? ' disabled' : ''),
      onclick: () => this.triggerUpload()
    }, this.bgImageBusy ? m("span", null, m("i", {
      className: "fas fa-spinner fa-spin"
    }), " \u4E0A\u4F20\u4E2D\u2026") : m("span", null, "\u4E0A\u4F20\u81EA\u5B9A\u4E49\u56FE\u7247 \xB7 \u22642MB \xB7 jpg/png/webp")), this.bgImageErr && m("div", {
      className: "ZS-bgimage-err"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), this.bgImageErr), m("div", {
      className: "ZS-bgimage-tip"
    }, builtinsCount, " \u5F20\u5185\u7F6E + ", customsCount, " \u5F20\u81EA\u5B9A\u4E49\uFF0C\u80CC\u666F\u533A\u57DF\u53EF\u4E0A\u4E0B\u6EDA\u52A8\u3002\u80CC\u666F\u56FE\u4F1A\u81EA\u52A8\u53E0\u4E00\u5C42\u6DF1\u8272\u906E\u7F69, \u4FDD\u8BC1\u5361\u7247\u6587\u5B57\u6E05\u6670\u53EF\u8BFB\u3002\u81EA\u5B9A\u4E49\u56FE\u60AC\u505C\u540E\u70B9\u53F3\u4E0A\u89D2 \xD7 \u5373\u53EF\u5220\u9664, \u65B0\u4E0A\u4F20\u7684\u56FE\u81EA\u52A8\u6392\u5728\u6700\u524D\u9762\u3002"));
  }
  content() {
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-modal-form"
    }, m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u540D\u79F0", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), m("input", {
      className: "ZS-form-input",
      value: this.form.name,
      oninput: ev => {
        this.form.name = ev.target.value;
        m.redraw();
      }
    })), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u8BFE\u7A0B\u5206\u7C7B", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*"), m("span", {
      style: "color:var(--zs-text-2-8d96a3, #8d96a3);font-weight:400;font-size:11px;margin-left:6px;"
    }, "(\u53EF\u591A\u9009)")), this.lessonCategoriesCheckboxList()), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u63CF\u8FF0"), m("textarea", {
      className: "ZS-form-input",
      rows: "2",
      placeholder: "\u4F8B\u5982: 1\u5BF91 \u79C1\u6559\u8BFE\u7A0B, \u542B 30 \u8BFE\u65F6",
      value: this.form.desc || '',
      oninput: ev => {
        this.form.desc = ev.target.value;
        m.redraw();
      }
    })), m("div", {
      className: "ZS-form-row"
    }, this.bgImagePicker()), m("div", {
      className: "ZS-grid-2col"
    }, m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u4EF7\u683C", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), m("input", {
      type: "number",
      className: "ZS-form-input",
      value: this.form.price,
      oninput: ev => {
        this.form.price = +ev.target.value;
        m.redraw();
      }
    })), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u8BFE\u65F6\u6570", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), m("input", {
      type: "number",
      className: "ZS-form-input",
      value: this.form.lessons,
      oninput: ev => {
        this.form.lessons = +ev.target.value;
        m.redraw();
      }
    }))), m("div", {
      className: "ZS-grid-2col"
    }, m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u65F6\u957F (\u5206\u949F)", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), m("input", {
      type: "number",
      className: "ZS-form-input",
      value: this.form.minutes,
      oninput: ev => {
        this.form.minutes = +ev.target.value;
        m.redraw();
      }
    })), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u8BFE\u7A0B\u7C7B\u578B", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), this.lessonTypeDropdown())), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u72B6\u6001"), m("div", {
      className: "ZS-switch-wrap"
    }, m("span", {
      style: {
        flex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
      }
    }, m("i", {
      className: "fas fa-circle-check",
      style: {
        color: this.form.status === 'enabled' ? '#2d7be5' : 'var(--zs-text-2-8d96a3, #8d96a3)'
      }
    }), this.form.status === 'enabled' ? '启用' : '停用'), m("label", {
      className: "ZS-Switch"
    }, m("input", {
      type: "checkbox",
      checked: this.form.status === 'enabled',
      onchange: e => {
        this.form.status = e.target.checked ? 'enabled' : 'disabled';
        m.redraw();
      }
    }), m("span", {
      className: "ZS-Switch-slider"
    })))), this.error && m("div", {
      className: "ZS-newversion-error",
      style: "margin-top:8px;"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
    }), this.error))), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving,
      onclick: () => this.submit()
    }, this.saving ? '保存中...' : '保存')));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/CardTemplateEditorModal', CardTemplateEditorModal);